import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Gavel, Trash2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { TypeBadge } from "./Signalements";
import RecidivesView from "../components/RecidivesView";
import { IncidentsBulletin } from "../components/IncidentsBulletin";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function SanctionCard({ r, confirmDelete, onOpenDialog, onAskDelete, onConfirmDelete }) {
  return (
    <div className="bg-white border border-[var(--parchment-2)] p-5" data-testid={`sanction-card-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-serif-display text-lg font-semibold">
            {r.student?.last_name.toUpperCase()} {r.student?.first_name}
          </span>
          <span className="text-sm text-[var(--ink-soft)]">{r.class?.name}</span>
          <TypeBadge type={r.type} label={r.type_label} />
          {r.status === "traite" && (
            <span className="px-2 py-0.5 text-xs font-medium text-white" style={{ background: "var(--ok)" }}>Traité</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            onClick={onOpenDialog}
            data-testid={`sanction-btn-${r.id}`}
          >
            <Gavel size={13} /> {r.sanction_text ? "Modifier" : "Sanction / remarque"}
          </button>
          {confirmDelete ? (
            <button className="!py-1.5 !px-3 text-xs bg-[var(--late)] text-white" onClick={onConfirmDelete} data-testid={`confirm-delete-sanction-${r.id}`}>Confirmer ?</button>
          ) : (
            <button className="text-[var(--muted)] hover:text-[var(--late)]" onClick={onAskDelete} data-testid={`delete-sanction-${r.id}`}>
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </div>
      <div className="mt-1 text-xs text-[var(--muted)] font-mono-spec">
        {r.date} · {r.subject?.name || "—"} · Signalé par {r.reported_by_user?.name || "—"}
      </div>
      {r.comment && <div className="mt-1.5 text-sm text-[var(--ink-soft)]">{r.comment}</div>}
      {r.sanction_text && (
        <div className="mt-2 text-sm bg-[var(--parchment)] border-l-2 border-[var(--maroon)] px-3 py-2">
          <span className="eyebrow !text-[var(--maroon)]">Décision :</span> {r.sanction_text}
          {r.sanction_by_user && <span className="text-xs text-[var(--muted)]"> — {r.sanction_by_user.name}</span>}
        </div>
      )}
    </div>
  );
}

function SanctionsList({ loading, rows, filter, confirmId, setConfirmId, setDialog, onRemove }) {
  if (loading) {
    return <div className="mt-8 text-[var(--ink-soft)]">Chargement...</div>;
  }
  return (
    <div className="mt-6 space-y-3" data-testid="sanctions-list">
      {rows.map((r) => (
        <SanctionCard
          key={r.id}
          r={r}
          confirmDelete={confirmId === r.id}
          onOpenDialog={() => setDialog({ id: r.id, text: r.sanction_text || "", label: `${r.student?.last_name.toUpperCase()} ${r.student?.first_name}` })}
          onAskDelete={() => setConfirmId(r.id)}
          onConfirmDelete={() => onRemove(r.id)}
        />
      ))}
      {rows.length === 0 && (
        <div className="py-8 text-center text-[var(--ink-soft)] italic bg-white border border-[var(--parchment-2)]">
          Aucun signalement {{ open: "à traiter", traite: "traité" }[filter] || ""}.
        </div>
      )}
    </div>
  );
}

export default function Sanctions() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [dialog, setDialog] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/incidents");
    setIncidents(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCount = incidents.filter((i) => i.status === "open").length;
  const rows = incidents.filter((i) => (filter === "all" ? true : i.status === filter));

  const saveSanction = async () => {
    if (!dialog.text.trim()) {
      toast.error("Saisissez la sanction ou remarque");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/incidents/${dialog.id}/sanction`, { text: dialog.text });
      toast.success("Sanction / remarque enregistrée");
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/incidents/${id}`);
      setConfirmId(null);
      toast.success("Signalement supprimé");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/send-incidents-digest");
      if (data.sent) toast.success(`Notification envoyée (${data.count} signalement${data.count > 1 ? "s" : ""})`);
      else if (data.reason === "empty") toast.info("Aucun nouveau signalement à notifier aujourd'hui");
      else toast.error(data.error || "Échec de l'envoi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="sanctions-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
          <div>
            <div className="eyebrow">Direction · Discipline</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Sanctions & remarques</h1>
            <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
              Signalements des professeurs (travail non fait, affaires oubliées, comportement). Notez la sanction
              ou remarque décidée — le professeur la verra dans son espace. Une notification email
              est envoyée en fin de matinée et en fin d'après-midi.
            </p>
          </div>
          <button className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2" onClick={sendDigest} data-testid="send-incidents-digest-btn">
            <Send size={14} /> Notifier maintenant
          </button>
        </div>

        <IncidentsBulletin />

        <div className="mt-6 flex flex-wrap gap-2" data-testid="sanctions-filter">
          <FilterChip active={filter === "open"} label={`À traiter (${openCount})`} onClick={() => setFilter("open")} testId="filter-open" />
          <FilterChip active={filter === "traite"} label="Traités" onClick={() => setFilter("traite")} testId="filter-traite" />
          <FilterChip active={filter === "all"} label="Tous" onClick={() => setFilter("all")} testId="filter-all" />
          <FilterChip active={filter === "recidives"} label="Récidives" onClick={() => setFilter("recidives")} testId="filter-recidives" />
        </div>

        {filter === "recidives" ? (
          <RecidivesView />
        ) : (
          <SanctionsList
            loading={loading}
            rows={rows}
            filter={filter}
            confirmId={confirmId}
            setConfirmId={setConfirmId}
            setDialog={setDialog}
            onRemove={remove}
          />
        )}
      </main>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Sanction / remarque · {dialog?.label}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <textarea
              rows={4}
              className={inputCls}
              value={dialog.text}
              onChange={(e) => setDialog((d) => ({ ...d, text: e.target.value }))}
              placeholder="Ex. Retenue mercredi prochain / Mot dans le carnet / Avertissement oral..."
              data-testid="sanction-text-input"
            />
          )}
          <DialogFooter>
            <button className="btn-secondary" onClick={() => setDialog(null)}>Annuler</button>
            <button className="btn-primary" onClick={saveSanction} disabled={busy} data-testid="sanction-save-btn">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {label}
    </button>
  );
}
